/**
 * The checks-list / current-state page (Req 12.1). Calls `GET /v1/checks` and renders each Check
 * with its current Check_State (up/down), latest result status, type, and locations. Handles the
 * loading, error, and empty states explicitly. Each row links to the check-detail route (pass B).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import { useApiClient } from '../api/use-api-client';
import type { CheckWithState } from '../api/types';
import { useCanWrite } from '../auth/use-can-write';
import { OriginBadge, StateBadge } from '../components/Badges';
import { InfoTooltip } from '../components/InfoTooltip';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string; isAuthError: boolean }
  | { status: 'loaded'; rows: CheckWithState[] };

export function ChecksListPage(): JSX.Element {
  const client = useApiClient();
  const canWrite = useCanWrite();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    client
      .listChecks()
      .then((response) => {
        if (active) setState({ status: 'loaded', rows: response.checks });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Failed to load checks';
        const isAuthError = error instanceof ApiError && error.isAuthError;
        setState({ status: 'error', message, isAuthError });
      });
    return () => {
      active = false;
    };
  }, [client]);

  return (
    <section>
      <div className="section-header">
        <h1>Checks</h1>
        {/* Write affordances render for editor/admin only (granular-crud-api Req 11.7). Generation
            is also a write action, so it is gated by the same flag (ai-check-generation Req 9.7).
            It lives here as well as on the drafts page because this is where someone comes to add a
            check, and a create affordance nobody finds is not a feature. */}
        {canWrite ? (
          <div className="section-actions">
            <Link to="/checks/new">New check</Link>
            <span className="section-action-with-hint">
              <Link to="/checks/generate">Generate from docs</Link>
              <InfoTooltip label="About generating checks from documentation">
                Point Soroush at API documentation — an uploaded file, a URL, or pasted text — and a
                model drafts a check from it: the endpoint to call, how often, and what counts as
                healthy. Nothing is created until you review the draft and accept it, and an
                accepted draft becomes an ordinary check you can edit like any other.
              </InfoTooltip>
            </span>
          </div>
        ) : null}
      </div>
      <ChecksListBody state={state} />
    </section>
  );
}

function ChecksListBody({ state }: { state: LoadState }): JSX.Element {
  if (state.status === 'loading') {
    return (
      <p role="status" className="muted">
        Loading checks…
      </p>
    );
  }

  if (state.status === 'error') {
    return (
      <div role="alert" className="error-panel">
        <p>{state.isAuthError ? 'Your session expired. Please sign in again.' : 'Could not load checks.'}</p>
        <p className="muted">{state.message}</p>
      </div>
    );
  }

  if (state.rows.length === 0) {
    return <p className="muted">No checks have been deployed for this account yet.</p>;
  }

  return (
    <table className="checks-table">
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Type</th>
          <th scope="col">State</th>
          <th scope="col">Latest</th>
          <th scope="col">Locations</th>
          <th scope="col">Managed by</th>
        </tr>
      </thead>
      <tbody>
        {state.rows.map(({ check, state: checkState }) => (
          <tr key={check.checkId}>
            <td>
              <Link to={`/checks/${encodeURIComponent(check.checkId)}`}>{check.logicalName}</Link>
            </td>
            <td>{check.type}</td>
            <td>
              <StateBadge state={checkState.state} hasResults={checkState.hasResults} />
            </td>
            <td>{checkState.latestStatus ?? '—'}</td>
            <td>{check.locations.length > 0 ? check.locations.join(', ') : '—'}</td>
            <td>
              <OriginBadge origin={check.origin} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
